/**
 * GWD Web-Chat-Widget — Entry-Point.
 *
 * Registriert ein Custom Element <gwd-chat>. Loader-Skript fuegt das
 * Element via data-Attributes ein:
 *
 *   <script async src=".../widget.js" data-widget-id="..." data-api="..."></script>
 *
 * → dieses Skript ergaenzt automatisch ein <gwd-chat>-Element im
 * <body>. Das Custom Element attached eine Shadow-DOM und mountet die
 * React-Komponente <Assistant />.
 *
 * Robustheit fuer Progressive-Hydration-Plattformen (OnePage, etc.):
 * MutationObserver setzt das Element automatisch wieder ein, wenn
 * der Host-Builder den Body neu rendert.
 */

// Defensive shim: einige React-internen Module referenzieren `process`
// in nicht erreichbaren Fehler-Pfaden. Wenn die Host-Site selbst
// `process` nicht definiert (Browser-Standard), reicht ein leeres
// Object als Stub. process.env.NODE_ENV wird beim Build hart durch
// 'production' ersetzt (vite.config.ts), das hier ist nur defense-
// in-depth.
declare global {
  interface Window { process?: unknown }
}
if (typeof (globalThis as { process?: unknown }).process === 'undefined') {
  (globalThis as { process?: unknown }).process = { env: { NODE_ENV: 'production' }, emit: () => false };
}

import { createRoot, Root } from 'react-dom/client';
import { createElement } from 'react';
import { Assistant } from './Assistant';
import widgetCss from './styles.css?inline';

class GwdChatElement extends HTMLElement {
  private root: Root | null = null;

  static get observedAttributes() {
    return ['data-widget-id', 'data-api', 'data-lang', 'data-accent'];
  }

  connectedCallback() {
    // Shadow-DOM fuer CSS-Isolation gegen Host-Site.
    const shadow = this.attachShadow({ mode: 'open' });
    // Inline-CSS injection
    const style = document.createElement('style');
    style.textContent = widgetCss;
    shadow.appendChild(style);
    const mount = document.createElement('div');
    mount.className = 'gwd-chat-root';
    shadow.appendChild(mount);

    const widgetId = this.getAttribute('data-widget-id') ?? '';
    const apiBase = this.getAttribute('data-api') ?? '';
    const lang = (this.getAttribute('data-lang') ?? '') as 'de' | 'en' | '';
    const accent = this.getAttribute('data-accent') ?? '';

    if (!widgetId) {
      console.error('[gwd-chat] data-widget-id fehlt');
      return;
    }
    if (!apiBase) {
      console.error('[gwd-chat] data-api fehlt');
      return;
    }

    this.root = createRoot(mount);
    this.root.render(
      createElement(Assistant, {
        widgetId,
        apiBase,
        initialLanguage: lang || undefined,
        accentOverride: accent || undefined,
        onNavigate: (path: string) => {
          // Default: same-origin navigation. Host-Site kann Event abfangen
          // (siehe README) und z.B. SPA-Router benutzen.
          const ev = new CustomEvent('gwd-chat:navigate', { detail: { path }, bubbles: true, composed: true });
          const cancelled = !this.dispatchEvent(ev);
          if (!cancelled) {
            try {
              window.location.assign(path);
            } catch {
              window.location.href = path;
            }
          }
        },
      }),
    );
  }

  disconnectedCallback() {
    this.root?.unmount();
    this.root = null;
  }
}

if (!customElements.get('gwd-chat')) {
  customElements.define('gwd-chat', GwdChatElement);
}

// Wir merken uns die Loader-Konfiguration global, damit
// re-mount nach DOM-Wipe ohne erneuten Skript-Lookup funktioniert.
let savedConfig: { widgetId: string; apiBase: string; lang: string | null; accent: string | null } | null = null;

/**
 * Erkennt die Sprache der Host-Site via, in dieser Reihenfolge:
 *   1. data-lang am Script-Tag (explizit gesetzt durch den Site-Admin)
 *   2. <html lang="…"> Attribut
 *   3. URL-Pattern: /en/, /de/, /it/ als erstes Path-Segment
 *   4. navigator.language des Browsers
 * Resultat ist immer ein 2-Zeichen-Code oder null.
 */
function detectLanguage(scriptDataLang: string | null): string | null {
  if (scriptDataLang) return scriptDataLang.slice(0, 2).toLowerCase();
  if (typeof document !== 'undefined') {
    const htmlLang = document.documentElement.getAttribute('lang');
    if (htmlLang) return htmlLang.slice(0, 2).toLowerCase();
  }
  if (typeof window !== 'undefined' && window.location?.pathname) {
    const seg = window.location.pathname.split('/').filter(Boolean)[0];
    if (seg && /^[a-z]{2}$/i.test(seg)) return seg.toLowerCase();
  }
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language.slice(0, 2).toLowerCase();
  }
  return null;
}

function readLoaderConfig(): typeof savedConfig {
  if (savedConfig) return savedConfig;
  if (typeof document === 'undefined') return null;
  // Erst data-Attribute am eigenen Skript lesen
  const scripts = Array.from(document.querySelectorAll('script[data-widget-id]'));
  const me = scripts.find((s) => (s as HTMLScriptElement).src.includes('widget.js')) as HTMLScriptElement | undefined;
  if (!me) return null;
  const widgetId = me.getAttribute('data-widget-id');
  const apiBase = me.getAttribute('data-api');
  if (!widgetId || !apiBase) return null;
  savedConfig = {
    widgetId,
    apiBase,
    lang: detectLanguage(me.getAttribute('data-lang')),
    accent: me.getAttribute('data-accent'),
  };
  return savedConfig;
}

function mountWidget() {
  if (typeof document === 'undefined' || !document.body) return;
  const cfg = readLoaderConfig();
  if (!cfg) return;
  // Schon vorhanden + im DOM angeschlossen? Skip.
  const existing = document.querySelector('gwd-chat');
  if (existing && document.body.contains(existing)) return;
  const el = document.createElement('gwd-chat');
  el.setAttribute('data-widget-id', cfg.widgetId);
  el.setAttribute('data-api', cfg.apiBase);
  if (cfg.lang) el.setAttribute('data-lang', cfg.lang);
  if (cfg.accent) el.setAttribute('data-accent', cfg.accent);
  document.body.appendChild(el);
}

// Re-Mount-Wachhund: einige Plattformen (OnePage, Builder etc.)
// hydratisieren progressiv und ersetzen Body-Inhalt. Wenn unser
// Element verschwindet, mounten wir es erneut. Throttled per
// requestAnimationFrame, damit der Observer nicht hot-loopt.
let scheduled = false;
function scheduleRemountCheck() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    mountWidget();
  });
}

function observeBody() {
  if (typeof MutationObserver === 'undefined' || !document.body) return;
  const obs = new MutationObserver(() => {
    if (!document.querySelector('gwd-chat')) scheduleRemountCheck();
  });
  obs.observe(document.body, { childList: true, subtree: false });
  // Stop nach 60s — danach ist Hydration sicher durch.
  setTimeout(() => obs.disconnect(), 60_000);
}

function init() {
  mountWidget();
  observeBody();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
// Window-load als zusaetzlicher Trigger fuer Plattformen die nach
// vollstaendigem Page-Load hydratisieren.
window.addEventListener('load', () => setTimeout(mountWidget, 100));
